import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { AppState, Platform } from '../types';
import { getPlatform, getQueryParam } from '../utils/helpers';

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PLATFORM'; payload: Platform }
  | { type: 'SET_BRAND_ID'; payload: string | null }
  | { type: 'SET_USER_ID'; payload: string | null }
  | { type: 'SET_BRAND_ASSETS'; payload: AppState['brandAssets'] }
  | { type: 'SET_FORM_FIELDS'; payload: AppState['formFields'] };

const initialState: AppState = {
  platform: 'google',
  brandId: null,
  userId: null,
  brandAssets: null,
  formFields: null,
  isLoading: true,
  error: null,
};

const AppContext = createContext<AppContextType | undefined>(undefined);

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_PLATFORM':
      return { ...state, platform: action.payload };
    case 'SET_BRAND_ID':
      return { ...state, brandId: action.payload };
    case 'SET_USER_ID':
      return { ...state, userId: action.payload };
    case 'SET_BRAND_ASSETS':
      return { ...state, brandAssets: action.payload };
    case 'SET_FORM_FIELDS':
      return { ...state, formFields: action.payload };
    default:
      return state;
  }
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Détecter la plateforme
        const platform = await getPlatform();
        dispatch({ type: 'SET_PLATFORM', payload: platform });

        // Récupérer les paramètres de l'URL
        const brandId = getQueryParam('brandId');
        const userId = getQueryParam('userId');

        dispatch({ type: 'SET_BRAND_ID', payload: brandId });
        dispatch({ type: 'SET_USER_ID', payload: userId });

        dispatch({ type: 'SET_LOADING', payload: false });
      } catch (error) {
        dispatch({
          type: 'SET_ERROR',
          payload: 'Erreur lors de l\'initialisation de l\'application',
        });
      }
    };

    initializeApp();
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
